import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  MoreHorizontal,
  Calendar,
  Target,
  DollarSign,
} from 'lucide-react';
import { Card, CardContent } from '../../components/Card';
import { Badge, getStatusVariant } from '../../components/Badge';
import { Button } from '../../components/Button';
import { campaignsApi } from '../../lib/api';
import { format } from 'date-fns';
import type { Campaign, CampaignStatus, CampaignType } from '../../types';

const STATUS_OPTIONS: CampaignStatus[] = ['draft', 'active', 'paused', 'completed'];
const TYPE_OPTIONS: CampaignType[] = ['content', 'email', 'social', 'paid', 'event'];

// Format currency
const formatCurrency = (value: number | null | undefined) => {
  if (!value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export function Campaigns() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['campaigns', statusFilter, typeFilter],
    queryFn: () =>
      campaignsApi.list({
        status: statusFilter || undefined,
        campaign_type: typeFilter || undefined,
        limit: 100,
      }),
  });

  const updateCampaignMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CampaignStatus }) =>
      campaignsApi.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const campaigns = (data?.campaigns || []) as Campaign[];

  // Filter by search query
  const filteredCampaigns = campaigns.filter((campaign) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      campaign.name.toLowerCase().includes(query) ||
      campaign.description?.toLowerCase().includes(query)
    );
  });

  // Calculate stats
  const stats = {
    total: campaigns.length,
    active: campaigns.filter((c) => c.status === 'active').length,
    totalBudget: campaigns.reduce((sum, c) => sum + (c.budget || 0), 0),
    totalSpent: campaigns.reduce((sum, c) => sum + (c.spent || 0), 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-gray-500 mt-1">Manage your marketing campaigns</p>
        </div>
        <Button>
          <Plus className="w-4 h-4" />
          New Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Campaigns</p>
            <p className="text-2xl font-semibold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-semibold text-green-600">{stats.active}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Budget</p>
            <p className="text-2xl font-semibold">{formatCurrency(stats.totalBudget)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Spent</p>
            <p className="text-2xl font-semibold text-blue-600">
              {formatCurrency(stats.totalSpent)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Types</option>
          {TYPE_OPTIONS.map((type) => (
            <option key={type} value={type}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Campaigns Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="text-center py-12">
          <Target className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No campaigns found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign) => (
            <Card key={campaign.id} className="hover:shadow-md transition-shadow">
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
                    {campaign.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {campaign.description}
                      </p>
                    )}
                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <MoreHorizontal className="w-4 h-4 text-gray-400" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant={getStatusVariant(campaign.status)}>{campaign.status}</Badge>
                  {campaign.campaign_type && (
                    <Badge variant="default">{campaign.campaign_type}</Badge>
                  )}
                  {campaign.pillar && (
                    <Badge variant="purple">{campaign.pillar.replace('_', ' ')}</Badge>
                  )}
                </div>

                {/* Goal Progress */}
                {campaign.goal_target && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-500">{campaign.goal_type} goal</span>
                      <span className="font-medium">
                        {campaign.goal_current || 0}/{campaign.goal_target}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            ((campaign.goal_current || 0) / campaign.goal_target) * 100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2 text-sm">
                  {(campaign.start_date || campaign.end_date) && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {campaign.start_date
                          ? format(new Date(campaign.start_date), 'MMM d')
                          : 'Start'}{' '}
                        -{' '}
                        {campaign.end_date
                          ? format(new Date(campaign.end_date), 'MMM d, yyyy')
                          : 'Ongoing'}
                      </span>
                    </div>
                  )}

                  {campaign.budget && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <DollarSign className="w-4 h-4" />
                      <span>
                        {formatCurrency(campaign.spent || 0)} /{' '}
                        {formatCurrency(campaign.budget)} budget
                      </span>
                    </div>
                  )}
                </div>

                {/* Quick status change */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <select
                    value={campaign.status}
                    onChange={(e) =>
                      updateCampaignMutation.mutate({
                        id: campaign.id,
                        status: e.target.value as CampaignStatus,
                      })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
